import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Heading, Text } from '@/components/ui/typography';
import { Stack, Inline } from '@/components/ui/layout';
import { cn } from '@/lib/utils';

const OtpModal = ({ visible, onVerify, onCancel, onResend, email }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (visible && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [visible]);

  const handleChange = (index, value) => {
    if (/^[0-9]?$/.test(value)) {
      const updatedOtp = [...otp];
      updatedOtp[index] = value;
      setOtp(updatedOtp);
      if (value && index < 5) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6); // Extract only digits, max 6
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i] || '';
      }
      setOtp(newOtp);
      
      // Focus on the next empty input or the last input
      const nextEmptyIndex = newOtp.findIndex((digit, idx) => !digit && idx < digits.length);
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(digits.length, 5);
      if (inputsRef.current[focusIndex]) {
        inputsRef.current[focusIndex].focus();
      }
    }
  };

  const handleVerifyClick = () => {
    onVerify(otp.join(''));
  };

  return (
    <Dialog open={visible} onOpenChange={onCancel}>
      <DialogContent className={cn("sm:max-w-[400px]")}>
        <DialogHeader>
          <DialogTitle><Heading level="h2">OTP VERIFICATION</Heading></DialogTitle>
          <DialogDescription>
            <Text>OTP has been sent to {email || 'your email'}</Text>
          </DialogDescription>
        </DialogHeader>

        <Stack spacing="md" align="center">
          <div className={cn("flex gap-2 justify-center")}>
            {otp.map((digit, index) => (
              <Input
                key={index}
                type="text"
                maxLength={1}
                value={digit}
                ref={(el) => (inputsRef.current[index] = el)}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={cn("w-12 h-12 text-center text-xl font-semibold")}
              />
            ))}
          </div>

          <Button variant="link" onClick={onResend}>Resend OTP</Button>
        </Stack>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleVerifyClick}>Verify</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OtpModal;
